import React, { useEffect, useRef, useState } from 'react'
import { Col, Form, Select as SelectAntd } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'
import produce from 'immer'

import { Button } from 'src/components/Ant'
import { Gender, RequestTicket } from 'src/types'
import {
  getInitialValues,
  getValidationSchema
} from 'src/core/forms/request/advance'
import { Option } from 'src/components/Formik/Select'
import { useStepsContext } from 'src/views/Requests/views/Form'
import { useT } from 'src/core/hooks/use-translation'
import useStandardResource from 'src/core/hooks/use-standard-resource'

import { WrapButtons } from './styles'
import AdvancesSummary from './components/AdvancesSummary'
import FormFields from './components/FormFields'

import { WrapCard, WrapRow } from '../../../styles'
import SegmentControl from '../SegmentControl'
import StepsNavigation from '../../../../StepsNavigation'

interface UserOption extends Option {
  gender?: Gender
}

const initialValues = getInitialValues()

export default function Advances() {
  const { t } = useT()

  const currencyStandard = useStandardResource('currencies')
  const financialResourceStandard = useStandardResource('financial_resources')

  const [userList, setUserList] = useState<UserOption[]>([])
  const stepsContext = useStepsContext()

  const refFormik = useRef(null)
  const refReceiverIdSelect = useRef<SelectAntd<string>>(null)

  useEffect(() => {
    setUserList([
      { label: t('web.services.advances.form.receiver.everyone'), value: '*' },
      ...stepsContext.request.request_user_data.map(item => ({
        label: `${item.traveler.name}`,
        value: item.traveler.user_id.toString(),
        gender: item.traveler.gender
      }))
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsContext.request.request_user_data])

  function handleResetForm() {
    setTimeout(() => {
      refFormik.current.resetForm({
        ...initialValues,
        request_ticket_title_advances: [
          {
            ...initialValues.request_ticket_title_advances[0],
            currency: currencyStandard,
            currency_id: currencyStandard
              ? currencyStandard.currency_id.toString()
              : null,
            financial_resource: financialResourceStandard,
            financial_resource_id: financialResourceStandard
              ? financialResourceStandard.financial_resource_id.toString()
              : null
          }
        ]
      })
    }, 100)
  }

  return (
    <Formik
      initialValues={{ ...initialValues }}
      onSubmit={values => {
        const policyRule = stepsContext.policyRule
        if (
          policyRule?.disable_advance_retroactive &&
          stepsContext.request?.requested_on <
            moment({
              hour: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0
            }).unix()
        ) {
          stepsContext.setAlert('services', {
            message: t('web.advance.message.disable-retroactive-advance'),
            type: 'warning'
          })
          return
        }

        stepsContext.setRequest(
          produce(stepsContext.request, draft => {
            if (values.request_ticket_id || values.tmp_id) {
              draft.request_tickets = draft.request_tickets.map(editedItem => ({
                ...editedItem,
                ...(editedItem.tmp_id === values.tmp_id &&
                editedItem.request_ticket_id === values.request_ticket_id
                  ? values
                  : {})
              }))
            } else if (values.receiver_id === '*') {
              userList.forEach(userListItem => {
                if (userListItem.value !== '*') {
                  draft.request_tickets.push({
                    ...values,
                    ...(values.request_ticket_id
                      ? {}
                      : { tmp_id: Math.random() }),
                    request_ticket_user_data: [
                      {
                        included: true,
                        owner: true,
                        request_ticket_id: null,
                        request_ticket_user_data_id: null,
                        traveler: {
                          gender: userListItem.gender,
                          name: userListItem.label,
                          user_id: userListItem.value
                        },
                        user_id: userListItem.value
                      }
                    ]
                  })
                }
              })
            } else {
              const receiver = userList.find(
                receiverItem => +receiverItem.value === +values.receiver_id
              )

              draft.request_tickets.push({
                ...values,
                ...(values.request_ticket_id ? {} : { tmp_id: Math.random() }),
                request_ticket_user_data: [
                  {
                    included: true,
                    owner: true,
                    request_ticket_id: null,
                    request_ticket_user_data_id: null,
                    traveler: {
                      gender: receiver.gender,
                      name: receiver.label,
                      user_id: receiver.value
                    },
                    user_id: receiver.value
                  }
                ]
              })
            }
          })
        )

        handleResetForm()

        refReceiverIdSelect.current.focus()
      }}
      ref={refFormik}
      render={formikProps => {
        const isTempId =
          !!formikProps.values.request_ticket_id || !!formikProps.values.tmp_id
        return (
          <Form onSubmit={formikProps.handleSubmit}>
            <StepsNavigation />
            <WrapRow>
              <Col span={15}>
                <WrapCard
                  bodyStyle={{ minHeight: 320 }}
                  title={<SegmentControl />}
                >
                  <FormFields
                    formikProps={formikProps}
                    refReceiverIdSelect={refReceiverIdSelect}
                    request={stepsContext.request}
                  />
                  <Form.Item>
                    <WrapButtons>
                      <div>
                        <Button
                          block
                          data-test-id="advance-action"
                          htmlType="submit"
                          icon={isTempId ? 'edit' : ''}
                          title={
                            isTempId
                              ? 'common.actions.change'
                              : 'common.actions.save'
                          }
                          type="primary"
                        />
                      </div>
                      {isTempId && (
                        <div>
                          <Button
                            block
                            onClick={() => handleResetForm()}
                            type="dashed"
                            title="common.actions.cancel-edit"
                          />
                        </div>
                      )}
                    </WrapButtons>
                  </Form.Item>
                </WrapCard>
              </Col>
              <Col offset={1} span={8}>
                <AdvancesSummary
                  bodyStyle={{ height: 320, overflow: 'auto' }}
                  handleDelete={(itemDelete: RequestTicket) => {
                    stepsContext.setRequest(
                      produce(stepsContext.request, draft => {
                        if (itemDelete.tmp_id) {
                          draft.request_tickets = draft.request_tickets.filter(
                            deletedItem =>
                              deletedItem.tmp_id !== itemDelete.tmp_id
                          )
                        }
                      })
                    )

                    handleResetForm()
                  }}
                  handleEdit={(itemEdit: RequestTicket) =>
                    formikProps.resetForm(itemEdit)
                  }
                  request={stepsContext.request}
                />
              </Col>
            </WrapRow>
          </Form>
        )
      }}
      validationSchema={getValidationSchema()}
    />
  )
}
